import PageHeader from "@/components/layout/PageHeader";
import { Link } from "react-router-dom";
import {
  BatteryCharging,
  ShieldCheck,
  Zap,
  Activity,
  Truck,
  Anchor,
  Award,
  ArrowRight,
  LifeBuoy,
  ClipboardCheck,
  CalendarCheck,
} from "lucide-react";
import { BATTERY_TYPES, IMAGES, BRAND, buildWhatsAppLink } from "@/data/site";
import Testimonials from "@/components/sections/Testimonials";
import ServiceChecklistSection from "@/components/sections/ServiceChecklistSection";
import BrandShowcaseSection from "@/components/sections/BrandShowcaseSection";

const SERIES_ICONS = [
  BatteryCharging,
  BatteryCharging,
  Zap,
  Zap,
  Truck,
  Anchor,
];

const HIGHLIGHT_FEATURES = [
  {
    icon: LifeBuoy,
    title: "6 Ay Ücretsiz Yol Yardımı",
    text:
      "Star Otogaz'dan akü hizmeti alan müşterilerimize 6 ay boyunca tamamen ücretsiz yol yardımı desteği sunuyoruz.",
  },
  {
    icon: ClipboardCheck,
    title: "Profesyonel Akü Kontrol Hizmeti",
    text:
      "Montaj öncesinde kaçak ölçümü, araç kapasite uygunluk testi, araç elektrik sistemi kontrolü, kutup başı oksit ve korozyon kontrolü ile eski akü ölçümü yapılır.",
  },
  {
    icon: CalendarCheck,
    title: "Garantili Yeni Tarihli Aküler",
    text:
      "Yeni üretim tarihli aküler takılır. Montaj sonrası tekrar ölçüm yapılır. BSG ve AGM Start-Stop akülerde 3 yıl, diğer tüm akülerde 2 yıl garanti sunulur.",
  },
];

export default function InciAkuPage() {
  return (
    <div data-testid="inci-aku-page">
      <PageHeader
        eyebrow="40 Yıllık Tecrübe"
        title="40 Yıllık Tecrübe ile"
        accent="Akü Hizmeti."
        subtitle="Star Otogaz olarak 40 yılı aşkın tecrübemizle Eskişehir ve çevresinde kaliteli akü satışı ve profesyonel montaj hizmeti sunuyoruz. Hizmet alan müşterilerimize 6 ay ücretsiz yol yardımı desteği sağlıyoruz."
      />

      {/* Hero brand block */}
      <section className="relative py-16 bg-gradient-to-b from-[#050A1F] via-[#020611] to-[#050A1F] overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-30" />
        <div className="absolute -top-20 right-0 w-[700px] h-[700px] rounded-full bg-inci-blue/20 blur-[140px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-inci-blue/20 blur-3xl rounded-full" />
            <div className="relative rounded-3xl overflow-hidden glass-blue glow-blue">
              <img
                src={IMAGES.battery}
                alt="İnci Akü, Varta, Bosch yetkili satış"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-4">
                <div className="glass-strong rounded-xl p-4">
                  <div className="text-gold text-[10px] uppercase tracking-[0.3em] font-semibold mb-1">
                    Yetkili Bayi
                  </div>
                  <div className="text-white font-display font-bold">
                    İnci Akü · Varta · Bosch
                  </div>
                </div>
                <div className="w-14 h-14 rounded-full bg-inci-blue flex items-center justify-center animate-float">
                  <Award className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mb-6 text-balance">
              <span className="text-gradient-silver">Dünyanın lider </span>
              <span
                className="text-inci-blue"
                style={{ filter: "drop-shadow(0 0 24px rgba(26,95,202,0.4))" }}
              >
                akü markaları.
              </span>
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Star Otogaz olarak{" "}
              <span className="text-gold font-semibold">İnci Akü</span>,{" "}
              <span className="text-gold font-semibold">Varta</span> ve{" "}
              <span className="text-gold font-semibold">Bosch</span> marka
              akülerin satış ve montajını gerçekleştiriyoruz. Tüm seriler —
              Start-Stop, AGM, EFB ve standart — stoğumuzda.
            </p>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {[
                { icon: ShieldCheck, t: "2-3 Yıl Üretici Garantisi" },
                { icon: Activity, t: "Ücretsiz Akü ve Şarj Testi" },
                { icon: Zap, t: "Tüm Araç Modelleri" },
                { icon: LifeBuoy, t: "6 Ay Ücretsiz Yol Yardımı" },
              ].map(({ icon: Icon, t }) => (
                <div
                  key={t}
                  className="flex items-center gap-3 p-3 rounded-xl glass-blue"
                >
                  <Icon className="w-4 h-4 text-inci-blue flex-shrink-0" />
                  <span className="text-sm text-white/90">{t}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={buildWhatsAppLink(
                  "Merhaba, İnci Akü / Varta / Bosch modelleri ve fiyatları hakkında bilgi almak istiyorum."
                )}
                target="_blank"
                rel="noreferrer"
                className="bg-inci-blue hover:bg-inci-blue-light text-white font-semibold px-7 py-3.5 inline-flex items-center justify-center gap-2 transition-all"
              >
                Akü Fiyat Sor <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href={`tel:${BRAND.phoneTel}`}
                className="border border-gold text-gold hover:bg-gold/10 font-semibold px-7 py-3.5 inline-flex items-center justify-center gap-2 transition-all"
              >
                Hemen Ara
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Highlight features (3 cards) */}
      <section
        className="relative py-24 overflow-hidden"
        data-testid="aku-highlights"
      >
        <div className="absolute inset-0 spotlight-gold opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Neden Star Otogaz?
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-balance">
              <span className="text-gradient-silver">Akü hizmetinde </span>
              <span className="text-gradient-gold">fark yaratıyoruz.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {HIGHLIGHT_FEATURES.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                data-testid={`aku-feature-${i}`}
                className="group glass rounded-2xl p-7 card-lift hover:border-gold/50"
              >
                <div className="w-14 h-14 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center mb-5 group-hover:bg-gold/20 group-hover:scale-110 transition-all duration-500">
                  <Icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3">
                  {title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServiceChecklistSection />
      <BrandShowcaseSection />

      {/* Battery series */}
      <section className="py-24 bg-[#050A1F] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
              Akü Çeşitleri
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-balance">
              <span className="text-gradient-silver">Aracınız için </span>
              <span className="text-gradient-gold">doğru seri.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BATTERY_TYPES.map((b, i) => {
              const Icon = SERIES_ICONS[i] || BatteryCharging;
              return (
                <div
                  key={b.title}
                  data-testid={`battery-type-${i}`}
                  className="glass-blue rounded-2xl p-7 card-lift hover:border-inci-blue/70"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-xl bg-inci-blue/20 border border-inci-blue/50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-inci-blue" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold">
                      Orijinal
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-xl text-white mb-2">
                    {b.title}
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-4">
                    {b.desc}
                  </p>
                  <div className="text-inci-blue font-mono text-sm font-semibold">
                    {b.feature}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-12 bg-inci-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white text-balance">
              Akünüz size en uygun mu? Ücretsiz test edelim.
            </h3>
            <p className="text-white/80 text-sm mt-2">
              Servisimize uğrayın, akünüzün performansını anında raporlayalım.
            </p>
          </div>
          <Link
            to="/iletisim"
            className="bg-gold hover:bg-gold-light text-black font-semibold px-7 py-3.5 inline-flex items-center gap-2 transition-all"
          >
            Hemen Randevu Al <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Testimonials />
    </div>
  );
}
